import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { MessengerService } from '../core/service/messenger.service';

@Injectable({
    providedIn: 'root'
})
export class CaseUtility implements OnDestroy
{
    /**
     * Default Priority options for Case.
     */
    public distinctPriorities = [
        { priorityKey: 'NORMAL', priorityName: 'Normal', priority: '50' },
        { priorityKey: 'URGENT', priorityName: 'Urgent', priority: '90' }
    ];

    public distinctStatus = [
        { statusKey: 'OPEN', statusName: 'Open', status: 'Open' },
        { statusKey: 'CLOSED', statusName: 'Closed', status: 'Closed' }
    ];

    public caseFilters: Array<{ text: string; value: string }> = [
        { text: 'Open', value: 'OPEN' },
        { text: 'Closed', value: 'CLOSED' },
        { text: 'All', value: 'ALL' }
    ];

    private messagingLanguageChangedSubscription: Subscription;

    constructor(
        private translate: TranslateService,
        private messengerService: MessengerService)
    {
        this.loadLocalizations();
        this.messagingLanguageChangedSubscription = this.messengerService.receiveMessage().subscribe({
            next: (message) =>
            {
                if (message && message.type && message.type === 'languageChanged')
                {
                    this.loadLocalizations();
                }
            }
        });
    }

    public ngOnDestroy(): void
    {
        if (this.messagingLanguageChangedSubscription !== undefined) { this.messagingLanguageChangedSubscription.unsubscribe(); }
    }

    /**
     * Calculates the badge class based on priority.
     *
     * @param priority Case priority.
     */
    public priorityBadgeClass(priority: number): string
    {
        let priorityBadgeClass = 'profileLetters ';
        if (priority >= 90)
        {
            // Urgent
            priorityBadgeClass += 'bg-priority-urgent';
        }
        else if (priority >= 70)
        {
            // High
            priorityBadgeClass += 'bg-priority-high';
        }
        else if (priority >= 50)
        {
            // Medium
            priorityBadgeClass += 'bg-priority-medium';
        }
        else
        {
            // Unknown/Other
            priorityBadgeClass += 'bg-priority-low';
        }

        return priorityBadgeClass;
    }

    /**
     * Calculates the letter based on priority.
     *
     * @param priority Case priority.
     */
    public priorityLetter(priority: number): string
    {
        let priorityLetter = '';

        if (priority >= 90)
        {
            // Urgent
            priorityLetter = this.distinctPriorities[1].priorityName.charAt(0);
        }
        else if (priority >= 50)
        {
            // Normal
            priorityLetter = this.distinctPriorities[0].priorityName.charAt(0);
        }
        else
        {
            // Unknown/Other
            priorityLetter = '?';
        }

        return priorityLetter;
    }

    /**
     * Calculates the i18n key based on priority.
     *
     * @param priority Case priority.
     */
    public priorityKey(priority: number): string
    {
        let priorityLetter = '';

        if (priority >= 90)
        {
            // Urgent
            priorityLetter = 'URGENT';
        }
        else if (priority >= 50)
        {
            // Normal
            priorityLetter = 'NORMAL';
        }
        else
        {
            // Unknown/Other
            priorityLetter = '';
        }

        return priorityLetter;
    }

    /**
     * Load localizations
     */
    private loadLocalizations(): void
    {
        this.translate.get(['NORMAL', 'URGENT']).subscribe({
            next: (res) =>
            {
                let index = 0;
                for (const key in res)
                {
                    if ({}.hasOwnProperty.call(res, key))
                    {
                        this.distinctPriorities[index].priorityName = res[key];
                        index++;
                    }
                }
            }
        });

        this.translate.get(['OPEN', 'CLOSED']).subscribe({
            next: (res) =>
            {
                let index = 0;
                for (const key in res)
                {
                    if ({}.hasOwnProperty.call(res, key))
                    {
                        this.distinctStatus[index].statusName = res[key];
                        index++;
                    }
                }
            }
        });

        this.translate.get('CASE-LIST-FILTERS').subscribe({
            next: (res: string) =>
            {
                const splitted = res.split(',');
                for (let i = 0; i < splitted.length; i++)
                {
                    if (this.caseFilters.length > i)
                    {
                        this.caseFilters[i].text = splitted[i];
                    }
                }
            }
        });
    }
}
